import { Currencies } from '@/entities/curency/model/types'
import { UpdateFarmModel } from '@/entities/farm'
import LimitIcon from '@/shared/assets/icons/farm/limit.svg?react'
import { BaseDialog, BaseInput, WeekdaySelect } from '@/shared/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { RequisitFarmProfile } from 'syncra-api-client'

export const convertEmptyStringsToNull = (obj: any): any => {
  const result = { ...obj }
  Object.keys(result).forEach((key) => {
    if (result[key] === '' || result[key] === undefined) {
      result[key] = null
    } else if (typeof result[key] === 'string' && !isNaN(Number(result[key]))) {
      const num = Number(result[key])
      if (!isNaN(num)) {
        result[key] = num
      }
    }
  })
  return result
}

type Props<T extends 'create' | 'update' = 'create'> = {
  modalVisible: boolean
  setModalVisible: (value: boolean) => void
  onSubmit: (data: T extends 'update' ? UpdateFarmModel :  RequisitFarmProfile) => void
  type?: T
  farmData?:  RequisitFarmProfile
  isLoading?: boolean
}

export const DialogFarmActions = <T extends 'create' | 'update' = 'create'>({
  modalVisible,
  setModalVisible,
  onSubmit,
  type = 'create' as T,
  farmData,
  isLoading,
}: Props<T>) => {
  const getInitialValues = () => ({
    name: undefined,
    maxDailyLimit: undefined,
    maxMonthlyLimit: undefined,
    maxDailyTopupsCounter: undefined,
    maxMonthlyTopupsCounter: undefined,
    minTimeoutBetweenDealsInMinutes: undefined,
    minDealAmount: undefined,
    maxDealAmount: undefined,
    dayOfWeek: undefined,
    workHoursStartsAt: undefined,
    workHoursEndsAt: undefined,
  })
  const { register, handleSubmit, control, reset } = useForm< RequisitFarmProfile>({
    defaultValues: { ...farmData },
  })

  useEffect(() => {
    if (farmData) {
      reset({ ...farmData })
    }
  }, [farmData])

  useEffect(() => {
    if (modalVisible) {
      if (type === 'update' && farmData) {
        reset({ ...farmData })
      } else {
        reset(getInitialValues())
      }
    }
  }, [modalVisible, type, farmData, reset])

  const isCreate = type === 'create'

  const handleClose = () => {
    setModalVisible(false)
  }

  const handleFormSubmit = (data:  RequisitFarmProfile) => {
    const processedData = convertEmptyStringsToNull(data)
    
    processedData.currency = Currencies.RUB;

    if (type === 'update' && farmData?.id) {
      const updateData: UpdateFarmModel = { ...processedData, id: farmData.id }
      onSubmit(updateData)
    } else {
      onSubmit(processedData)
    }
  }

  return (
    <BaseDialog
      isVisible={modalVisible}
      onClose={handleClose}
      onAction={handleSubmit(handleFormSubmit)}
      title={isCreate ? 'Создание фермы' : 'Редактрирование фермы'}
      actionButtonName={isCreate ? 'Сохранить' : 'Изменить'}
      isLoading={isLoading}
    >
      <form className="flex flex-col gap-y-4 p-0.5 max-h-[540px] overflow-y-auto scrollbar-hide">
        <BaseInput
          label={isCreate ? 'Придумайте название' : 'Изменить название'}
          placeholder="Название"
          {...register('name')}
          variant="secondary"
        />
        <div className="flex items-center gap-2">
          <h3 className="text-lg">Настройки реквизитов</h3>
          <LimitIcon className="text-primary dark:text-primary-dark" />
        </div>
        <div className="gap-4 grid grid-cols-2">
          <BaseInput
            label="Суточный лимит"
            placeholder="Укажите в RUB"
            variant="secondary"
            {...register('maxDailyLimit')}
          />
          <BaseInput
            label="Месячный лимит"
            placeholder="Укажите в RUB"
            variant="secondary"
            {...register('maxMonthlyLimit')}
            toolpitText="Максимальный объём средств, который может быть принят на данный реквизит в течение календарного месяца."
          />
        </div>
        <div className="gap-4 grid grid-cols-2">
          <BaseInput
            label="Сделки в день"
            placeholder="Укажите"
            variant="secondary"
            {...register('maxDailyTopupsCounter')}
          />
          <BaseInput
            label="Сделки в месяц"
            placeholder="Не менее 3 000"
            variant="secondary"
            {...register('maxMonthlyTopupsCounter')}
            toolpitText="Ограничение на количество заявок, которое может быть принято на реквизит в течение месяца."
          />
        </div>
        <BaseInput
          label="Интервал между сделками (мин.)"
          placeholder="0 мин"
          variant="secondary"
          {...register('minTimeoutBetweenDealsInMinutes')}
        />
        <div className="items-end gap-4 grid grid-cols-2">
          <BaseInput
            label="Сумма сделки"
            placeholder="Мин. сумма"
            variant="secondary"
            {...register('minDealAmount')}
          />
          <BaseInput placeholder="Макс. сумма" variant="secondary" {...register('maxDealAmount')} />
        </div>
        <div>
          <p className="mb-2">Дни работы</p>
          <Controller
            name="dayOfWeek"
            control={control}
            render={({ field }) => (
              <WeekdaySelect
                {...field}
                value={field.value ?? undefined}
                onChange={(val) => field.onChange(val ?? null)}
              />
            )}
          />
        </div>

        <div className="items-end gap-4 grid grid-cols-2">
          <BaseInput
            label="Время работы"
            placeholder="00:00"
            variant="secondary"
            {...register('workHoursStartsAt')}
            toolpitText="Интервал времени, в который реквизит принимает заявки по московскому времени (МСК)."
          />
          <BaseInput
            label=""
            placeholder="00:00"
            variant="secondary"
            {...register('workHoursEndsAt')}
          />
        </div>
      </form>
    </BaseDialog>
  )
}
